import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";
import { CreateVendorSchema, UpdateVendorSchema } from "./vendor.schema";

export const VendorIdInputSchema = z.object({
  id: z.string().trim().min(1),
  businessId: z.string().trim().min(1),
});

export const ListVendorsInputSchema = z.object({
  businessId: z.string().trim().min(1),
  search: z.string().optional(),
  offset: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(200).optional(),
  ids: z.array(z.string().trim().min(1)).max(500).optional(),
});

export const CreateVendorInputSchema = z.object({
  businessId: z.string().trim().min(1),
  item: CreateVendorSchema,
});

export const UpdateVendorInputSchema = z.object({
  ...VendorIdInputSchema.shape,
  updates: UpdateVendorSchema,
});

export const listVendors = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => ListVendorsInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("vendors.list", async () => {
      const { listVendorsForUser } = await import("./vendor.server");
      return listVendorsForUser(context.user.id, data.businessId, {
        search: data.search,
        offset: data.offset,
        limit: data.limit,
        ids: data.ids,
      });
    });
  });

export const getVendor = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => VendorIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("vendors.get", async () => {
      const { getVendorForUser } = await import("./vendor.server");
      const result = await getVendorForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const createVendor = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => CreateVendorInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("vendors.create", async () => {
      const { createVendorForUser } = await import("./vendor.server");
      return createVendorForUser(context.user.id, data.businessId, data.item);
    });
  });

export const updateVendor = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => UpdateVendorInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("vendors.update", async () => {
      const { updateVendorForUser } = await import("./vendor.server");
      const result = await updateVendorForUser(
        context.user.id,
        data.id,
        data.updates,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const deleteVendor = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => VendorIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("vendors.delete", async () => {
      const { deleteVendorForUser } = await import("./vendor.server");
      const result = await deleteVendorForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });
